import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBars,
  FaCog,
  FaDownload,
  FaHeadset,
  FaShoppingBag,
  FaSignInAlt,
  FaSignOutAlt,
  FaTimes,
  FaUserCircle,
  FaUserPlus
} from "react-icons/fa";
import RotatingModel from "./RotatingModel";
import ChatBotBubble from "./ChatBotBubble";
import { DOWNLOAD_URL } from "../data/siteCatalog";
import { resolveKyrosApiUrl } from "../services/apiConfig";
import "../style/BrandBar.css";
import kyrosLogo from "../images/kyros_name.png";

const DISCORD_URL = "https://discord.gg/sFxWsn4ruU";
const ACCOUNT_LOGIN_URL = "/account";
const ACCOUNT_REGISTER_URL = "/account/register";
const ACCOUNT_PROFILE_URL = "/account";
const STORE_URL = "/store";
const AUTH_STORAGE_KEY = "kyros_auth_v1";
const COMPACT_BREAKPOINT = 640;
const AUTH_LOGOUT_PATH = "/auth/logout";
const ACCOUNT_LOCKED_ICON_URL = "/images/kyros_logo.ico";

function readStoredAuth() {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY) || "null");
  } catch (_error) {
    return null;
  }
}

export default function BrandBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const brandBarRef = useRef(null);
  const accountMenuRef = useRef(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [authSnapshot, setAuthSnapshot] = useState(null);
  const [accountAvatarLoadError, setAccountAvatarLoadError] = useState(false);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateHeight = () => {
      if (!brandBarRef.current) return;
      const { height } = brandBarRef.current.getBoundingClientRect();
      document.documentElement.style.setProperty("--brand-bar-height", height + "px");
    };

    updateHeight();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => updateHeight());
      if (brandBarRef.current) {
        resizeObserver.observe(brandBarRef.current);
      }
    }

    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      document.documentElement.style.removeProperty("--brand-bar-height");
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mediaQuery = window.matchMedia("(max-width: " + COMPACT_BREAKPOINT + "px)");
    const handleChange = (event) => setIsCompact(event.matches);

    setIsCompact(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleMenuState = (event) => {
      setMenuOpen(Boolean(event && event.detail && event.detail.open));
    };

    window.addEventListener("topbar-menu-state", handleMenuState);
    window.dispatchEvent(new CustomEvent("topbar-request-state"));

    return () => window.removeEventListener("topbar-menu-state", handleMenuState);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncAuthSnapshot = () => {
      setAuthSnapshot(readStoredAuth());
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        syncAuthSnapshot();
      }
    };

    window.addEventListener("storage", syncAuthSnapshot);
    window.addEventListener("focus", syncAuthSnapshot);
    window.addEventListener("kyros-auth-updated", syncAuthSnapshot);
    document.addEventListener("visibilitychange", handleVisibility);

    syncAuthSnapshot();

    return () => {
      window.removeEventListener("storage", syncAuthSnapshot);
      window.removeEventListener("focus", syncAuthSnapshot);
      window.removeEventListener("kyros-auth-updated", syncAuthSnapshot);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    setAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!accountMenuOpen || typeof window === "undefined") {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setAccountMenuOpen(false);
      }
    };

    window.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [accountMenuOpen]);

  const openChat = useCallback(() => {
    setChatOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setChatOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("topbar-toggle-menu"));
    }
  }, []);

  const closeAccountMenu = useCallback(() => {
    setAccountMenuOpen(false);
  }, []);

  const toggleAccountMenu = useCallback(() => {
    setAccountMenuOpen(prev => !prev);
  }, []);

  const goToLoginLanding = useCallback(() => {
    if (typeof window !== "undefined") {
      const rawPath = String(window.location.pathname || "").trim();
      const normalizedPath = rawPath.replace(/\/+$/, "") || "/";
      if (normalizedPath === ACCOUNT_LOGIN_URL || normalizedPath.startsWith(`${ACCOUNT_LOGIN_URL}/`)) {
        window.location.assign(ACCOUNT_LOGIN_URL);
        return;
      }
    }
    navigate(ACCOUNT_LOGIN_URL, { replace: true });
  }, [navigate]);

  const goToLogin = useCallback(() => {
    closeAccountMenu();
    goToLoginLanding();
  }, [closeAccountMenu, goToLoginLanding]);

  const goToRegister = useCallback(() => {
    closeAccountMenu();
    navigate(ACCOUNT_REGISTER_URL);
  }, [closeAccountMenu, navigate]);

  const goToAccountSettings = useCallback(() => {
    closeAccountMenu();
    navigate(ACCOUNT_PROFILE_URL);
  }, [closeAccountMenu, navigate]);

  const goToStore = useCallback(() => {
    closeAccountMenu();
    navigate(STORE_URL);
  }, [closeAccountMenu, navigate]);

  const handleLogout = useCallback(async () => {
    const currentAuth = readStoredAuth();
    const refreshToken = currentAuth && currentAuth.refresh_token;

    if (refreshToken) {
      try {
        await fetch(resolveKyrosApiUrl(AUTH_LOGOUT_PATH), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken })
        });
      } catch (_error) {
        // Silent fallback: we still clear local session.
      }
    }

    if (typeof window !== "undefined") {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
      window.dispatchEvent(new CustomEvent("kyros-auth-updated"));
    }
    setAuthSnapshot(null);
    closeAccountMenu();
    goToLoginLanding();
  }, [closeAccountMenu, goToLoginLanding]);

  const hasActiveSession = Boolean(
    authSnapshot && (authSnapshot.access_token || authSnapshot.refresh_token)
  );
  const account = (authSnapshot && authSnapshot.account) || {};
  const accountEmail = String(account.email || "").trim().toLowerCase();
  const accountAvatarUrl = String(account.avatar_url || "").trim();
  const accountDisplayName =
    String(account.display_name || account.name || "").trim() ||
    (accountEmail ? accountEmail.split("@")[0] : "Kyros Player");
  const accountIdentityInitial =
    accountDisplayName.trim().charAt(0).toUpperCase() || "K";
  const hasRenderableAvatar =
    hasActiveSession && !!accountAvatarUrl && !accountAvatarLoadError;

  useEffect(() => {
    setAccountAvatarLoadError(false);
  }, [accountAvatarUrl]);

  const modelSize = isCompact ? 48 : 72;
  const modelOffset = isCompact ? 0 : 8;

  return (
    <>
      <div className="brand-bar" ref={brandBarRef}>
        <div className="brand-bar__identity">
          <button
            type="button"
            className="brand-bar__model"
            aria-label="Apri chat con Kyros Bot"
            onClick={openChat}
          >
            <RotatingModel
              width={modelSize}
              height={modelSize}
              offsetTop={modelOffset}
              onActivate={openChat}
              isChatOpen={chatOpen}
            />
          </button>
          <img
            src={kyrosLogo}
            alt="Kyros Games logo"
            className="brand-bar__logo"
          />
        </div>

        <div className="brand-bar__actions">
          <button
            type="button"
            className={`brand-bar__menu${menuOpen ? " brand-bar__menu--active" : ""}`}
            aria-label={menuOpen ? "Chiudi menu di navigazione" : "Apri il menu di navigazione"}
            aria-expanded={menuOpen}
            onClick={toggleMenu}
          >
            {menuOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
            <span className="brand-bar__sr-only">Menu</span>
          </button>

          <a
            href={DISCORD_URL || "#"}
            className="brand-bar__chip brand-bar__chip--icon brand-bar__chip--support"
            target={DISCORD_URL ? "_blank" : undefined}
            rel={DISCORD_URL ? "noopener noreferrer" : undefined}
            aria-label="Apri il canale Discord di Kyros Games"
            title="Discord"
          >
            <FaHeadset aria-hidden="true" />
          </a>

          <a
            href={DOWNLOAD_URL}
            className="brand-bar__chip brand-bar__chip--icon brand-bar__chip--launcher"
            download
            aria-label="Scarica il launcher Kyros Games"
            title="Scarica launcher"
          >
            <FaDownload aria-hidden="true" />
          </a>

          <div
            ref={accountMenuRef}
            className={`brand-bar__account${accountMenuOpen ? " brand-bar__account--open" : ""}`}
          >
            <button
              type="button"
              className="brand-bar__chip brand-bar__chip--icon brand-bar__chip--account"
              aria-label="Apri menu account"
              aria-expanded={accountMenuOpen}
              aria-haspopup="menu"
              aria-controls="brand-bar-account-menu"
              title={hasActiveSession ? "Menu account" : "Accedi o registrati"}
              onClick={toggleAccountMenu}
            >
              {hasActiveSession ? (
                <span className="brand-bar__account-chip-avatar" aria-hidden="true">
                  {hasRenderableAvatar ? (
                    <img
                      src={accountAvatarUrl}
                      alt=""
                      className="brand-bar__account-chip-avatar-image"
                      onError={() => setAccountAvatarLoadError(true)}
                    />
                  ) : (
                    <span className="brand-bar__account-chip-avatar-fallback">
                      {accountIdentityInitial}
                    </span>
                  )}
                </span>
              ) : (
                <FaUserCircle aria-hidden="true" />
              )}
            </button>

            {accountMenuOpen && (
              <div id="brand-bar-account-menu" className="brand-bar__account-dropdown" role="menu">
                {!hasActiveSession ? (
                  <>
                    <button
                      type="button"
                      className="brand-bar__account-primary brand-bar__account-action--centered"
                      role="menuitem"
                      onClick={goToLogin}
                    >
                      <FaSignInAlt aria-hidden="true" />
                      <span>Connettiti</span>
                    </button>
                    <button
                      type="button"
                      className="brand-bar__account-item brand-bar__account-action--centered"
                      role="menuitem"
                      onClick={goToStore}
                    >
                      <FaShoppingBag aria-hidden="true" />
                      <span>Store</span>
                    </button>
                    <button
                      type="button"
                      className="brand-bar__account-item brand-bar__account-action--centered"
                      role="menuitem"
                      onClick={goToRegister}
                    >
                      <FaUserPlus aria-hidden="true" />
                      <span>Crea un account</span>
                    </button>
                    <div className="brand-bar__account-locked" aria-hidden="true">
                      <div className="brand-bar__locked-dashboard" />
                      <div className="brand-bar__locked-overlay">
                        <img src={ACCOUNT_LOCKED_ICON_URL} alt="" className="brand-bar__locked-logo" />
                        <span className="brand-bar__locked-copy">
                          Un'unica identità. Tutti i tuoi progressi. Ovunque.
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="brand-bar__account-summary">
                      <div className="brand-bar__account-avatar" aria-hidden="true">
                        {hasRenderableAvatar ? (
                          <img
                            src={accountAvatarUrl}
                            alt=""
                            className="brand-bar__account-avatar-image"
                            onError={() => setAccountAvatarLoadError(true)}
                          />
                        ) : (
                          <span>{accountIdentityInitial}</span>
                        )}
                      </div>
                      <div className="brand-bar__account-meta">
                        <strong>{accountDisplayName}</strong>
                        <span>{accountEmail || "account@kyrosgames.com"}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="brand-bar__account-item"
                      role="menuitem"
                      onClick={goToAccountSettings}
                    >
                      <FaCog aria-hidden="true" />
                      <span>Impostazioni account</span>
                    </button>
                    <button
                      type="button"
                      className="brand-bar__account-item"
                      role="menuitem"
                      onClick={goToStore}
                    >
                      <FaShoppingBag aria-hidden="true" />
                      <span>Store</span>
                    </button>
                    <div className="brand-bar__account-divider" />
                    <button
                      type="button"
                      className="brand-bar__account-item brand-bar__account-item--danger"
                      role="menuitem"
                      onClick={handleLogout}
                    >
                      <FaSignOutAlt aria-hidden="true" />
                      <span>Disconnessione</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <ChatBotBubble open={chatOpen} onClose={closeChat} />
    </>
  );
}
